import React from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/compat/router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useCart from 'hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import { useExclusiveCollectionGate } from 'hooks/use-exclusive-collection-gate';

import { MobileOnly, DesktopOnly } from 'shared/components';
import Menu from 'components/menu';
import { MainDispensaryLayout } from 'components/layouts/main-dispensary-layout';
import { CategorySubcategoryPills } from 'components/category-subcategory-pills';
import { CollectionsHeader } from 'src/dispensary/collections/collections-header';
import { WhoopsContent, WhoopsReason } from 'src/components/whoops';
import { useCollections } from 'src/dispensary/collections/use-collections';
import { Typography } from 'src/typography';
import { CategorySubcategories } from 'src/components/menu-sidebar/category-subcategory-filter/types';
import { useRemoteComponent, RemoteComponentSlot } from 'hooks/use-remote-component';

type DispensaryCollectionsPageProps = {
  /**
   * When provided, overrides the `slug` route param. This lets other routes
   * (e.g. /products/:category-or-collection) render a custom category
   * collection without requiring a navigation to /collections/:slug.
   */
  slugOverride?: string;
};

export function DispensaryCollectionsPage({ slugOverride }: DispensaryCollectionsPageProps = {}): JSX.Element | null {
  const { dispensary } = useDispensary();
  const router = useRouter();
  const { menuType } = useCart();

  const flags = useFlags();
  const isOutletCollectionsEnabled = flags['ecomm.display-outlet-collections-consumer.rollout'] ?? false;

  const ListPageHero = useRemoteComponent(RemoteComponentSlot.ListPageHero);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
  const slug = slugOverride ?? ((router?.query?.slug as string) || '');
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
  const page = Number(router?.query?.page ?? 1);
  const limit = 30;
  const offset = (page - 1) * limit;

  const { collection, loading, error } = useCollections({
    collectionId: slug,
    dispensaryId: dispensary?.id,
    menuType,
    limit,
    offset,
  });

  const { categoriesSubcategories } = useMenuFilterOptions({ collectionId: collection?.id });

  const {
    cardBadgeColor,
    customCategory,
    description,
    image,
    isAccessibleToCurrentUser,
    isExclusive,
    isOutlet,
    outletTimerEnabled,
    outletTimerMessage,
    schedule,
    productCount,
    products,
    title,
  } = collection ?? {};

  const isForbiddenError = (error?.graphQLErrors ?? []).some((err: any) => err.extensions?.code === 'FORBIDDEN');

  useExclusiveCollectionGate({
    isExclusive: isForbiddenError ? true : isExclusive,
    isAccessibleToCurrentUser: isForbiddenError ? false : isAccessibleToCurrentUser,
    loading,
  });

  if (!dispensary) {
    return null;
  }

  // Don't show error page for FORBIDDEN errors - let the gate modal handle it
  if (!loading && !isForbiddenError && (error || !collection)) {
    return <WhoopsContent reason={WhoopsReason.serverError} />;
  }

  // While gate modal is handling FORBIDDEN error, show loading state
  if (isForbiddenError) {
    return null;
  }

  const collectionsHeader = (
    <CollectionsHeader
      title={title ?? ''}
      description={description}
      image={image}
      customCategory={customCategory}
      isExclusive={isExclusive}
      cardBadgeColor={cardBadgeColor}
      isOutlet={isOutlet}
      outletTimerEnabled={outletTimerEnabled}
      outletTimerMessage={outletTimerMessage}
      schedule={schedule}
    />
  );

  // Determine if outlet-specific UI behavior should be applied
  const showOutletBehavior = isOutletCollectionsEnabled && isOutlet;

  let heading = 'All Products';
  if (showOutletBehavior) {
    heading = '';
  } else if (customCategory) {
    heading = title ?? '';
  }

  return (
    <>
      <ListPageHero />
      <MenuWrapper>
        <MobileOnly>
          <CollectionsMobileHeaderWrapper>{collectionsHeader}</CollectionsMobileHeaderWrapper>

          {/* Don't show duplicate heading for outlet collections since it's in the header */}
          {!showOutletBehavior && (
            <MobileHeadingContainer>
              <CollectionsHeadingContainer>
                <Typography.Heading size='medium' tag='h1'>
                  {customCategory ? title : 'All Products'}
                </Typography.Heading>
              </CollectionsHeadingContainer>

              {!customCategory && (
                <CategorySubcategoryPills
                  categoriesSubcategories={categoriesSubcategories as CategorySubcategories[]}
                />
              )}
            </MobileHeadingContainer>
          )}
        </MobileOnly>
      </MenuWrapper>

      <Menu
        totalCount={productCount}
        totalPages={Math.ceil((productCount ?? 0) / limit)}
        products={products}
        loading={loading}
        dispensary={dispensary}
        lastCrumbLabel={title}
        heading={heading}
        description={showOutletBehavior ? '' : description}
        customHeaderComponent={<StyledDesktopOnly>{collectionsHeader}</StyledDesktopOnly>}
        page='collection'
        collectionId={collection?.id}
        customCategory={collection?.customCategory}
      />
    </>
  );
}

DispensaryCollectionsPage.layout = MainDispensaryLayout;

const MenuWrapper = styled.div`
  margin: 0 auto;
  max-width: var(--dct--customized-maxSiteWidth);
  width: 100%;
`;

const CollectionsMobileHeaderWrapper = styled.div`
  padding: 20px 16px 0;
`;

const CollectionsHeadingContainer = styled.div`
  padding: 0 0 0 16px;
`;

const MobileHeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: scroll;
  margin-bottom: -9px;
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;

const StyledDesktopOnly = styled(DesktopOnly)`
  width: 100%;
`;
